import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { sampleContent } from '../../assets/sampleContent/sampleContent';
import { IdioticStatementCard } from '../idioticCard/IdioticStatementCard';
import { setValue } from '../../actions';
import { getScrollOffset } from '../../utils/scrollOffset';
import { slugify } from '../../utils/slugify';
import { checkNo, mythPath, verdictCounts, badgeClass } from '../../utils/myths';
import { track, useSearchTracking } from '../../utils/track';

const TOPICS = ['EV fires', 'Safety', 'Politics & Policy', 'Cost', 'Battery life', 'Emissions', 'Tesla', 'EV sales', 'Charging', 'Subsidies', 'Child labor', 'Cold weather', 'Climate', 'Reliability', 'Range', 'Hybrid'];
const IDIOTS = ['Hungry Idiot', 'Curious Idiot', 'Angry Idiot'];
const VERDICT_TABS = [
  { key: 'all', label: 'All' },
  { key: 'false', label: 'False' },
  { key: 'true', label: 'True' },
  { key: 'misleading', label: 'Misleading' },
];

const scrollToEl = (el) => {
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - getScrollOffset();
  window.scrollTo({ top, behavior: 'smooth' });
};

// Bubbles shown before "Show all": full rows of the 3-column desktop and
// 2-column tablet grids, seven on a phone. All 34 ran about five screens.
const previewCount = () => (window.innerWidth <= 560 ? 7 : window.innerWidth <= 980 ? 8 : 9);

/**
 * The fact-check desk: the myths as thought bubbles, a few rows at first with a
 * "Show all" button. Popping one turns it into the full fact card (stamp,
 * numbers, receipts, sources, share). Search, topic/idiot filters and verdict
 * tabs narrow the grid.
 *
 * Contracts other code relies on (keep them):
 *  - every rendered myth wrapper carries data-card-slug={slugify(title)};
 *  - the fi:pop event (ticker, Roast-a-Comment's "See sources") pops and scrolls
 *    to any myth, and a popped myth renders even when it's past the preview;
 *  - /?s=<slug> share links pop and scroll to that myth;
 *  - a quiz result (redux quizDecision) filters the desk to that idiot type.
 */
export default function MythDesk({ selectedPart }) {
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState([]);
  const [verdict, setVerdict] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [popped, setPopped] = useState(() => new Set());
  const [allPopped, setAllPopped] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [preview, setPreview] = useState(previewCount);

  const deskRef = useRef(null);
  const searchRef = useRef(null);
  const filtersRef = useRef(null);
  const countRef = useRef(null);
  const cardRefs = useRef({});

  const quizDecision = useSelector((state) => state.quizDecision.value);
  const dispatch = useDispatch();

  const pop = useCallback((slug, scroll = true) => {
    setPopped((prev) => { const next = new Set(prev); next.add(slug); return next; });
    if (scroll) setTimeout(() => scrollToEl(cardRefs.current[slug]), 60);
  }, []);

  const unpop = (slug) => {
    setAllPopped(false);
    setPopped((prev) => { const next = new Set(prev); next.delete(slug); return next; });
  };

  // /?s=<slug> share links: pop the myth and bring it into view.
  useEffect(() => {
    const target = new URLSearchParams(window.location.search).get('s');
    if (!target) return undefined;
    const hit = sampleContent.find((s) => slugify(s.title) === slugify(target));
    if (!hit) return undefined;
    const slug = slugify(hit.title);
    pop(slug, false);
    const t = setTimeout(() => scrollToEl(cardRefs.current[slug]), 700);
    return () => clearTimeout(t);
  }, [pop]);

  // Roast-a-Comment "See sources" and the ticker ask the desk to pop a myth.
  useEffect(() => {
    const onPop = (e) => {
      const slug = e.detail;
      if (!slug) return;
      setSearchText('');
      setFilters([]);
      setVerdict('all');
      pop(slug);
    };
    window.addEventListener('fi:pop', onPop);
    return () => window.removeEventListener('fi:pop', onPop);
  }, [pop]);

  useEffect(() => {
    const onResize = () => setPreview(previewCount());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // A quiz result arrives as quizDecision: filter once, then clear it.
  useEffect(() => {
    if (quizDecision === '') return;
    setFilters([quizDecision]);
    setShowFilters(true);
    dispatch(setValue(''));
    setTimeout(() => scrollToEl(filtersRef.current), 900);
  }, [quizDecision, dispatch]);

  // Header nav: "Search" focuses the box, "Idiotic statements" scrolls to the desk.
  useEffect(() => {
    if (selectedPart === 'search') {
      scrollToEl(searchRef.current);
      setTimeout(() => { const input = searchRef.current && searchRef.current.querySelector('input'); if (input) input.focus(); }, 400);
    } else if (selectedPart === 'content') {
      scrollToEl(deskRef.current);
    }
  }, [selectedPart]);

  const toggleFilter = (option) => {
    setSearchText('');
    track('filter_change', { context: 'myth_desk', filter_type: TOPICS.includes(option) ? 'topic' : 'idiot_type', filter_value: option, on: !filters.includes(option) });
    setFilters((prev) => (prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]));
  };

  const results = useMemo(() => {
    let list = sampleContent;
    if (filters.length > 0) {
      list = list.filter((s) => filters.some((f) => s.idiotTypes.includes(f) || s.categories.includes(f)));
    }
    if (verdict !== 'all') {
      list = list.filter((s) => String(s.titleBadge).toLowerCase() === verdict);
    }
    const q = searchText.trim().toLowerCase();
    if (q) list = list.filter((s) => s.title.toLowerCase().includes(q));
    return list;
  }, [filters, verdict, searchText]);

  useSearchTracking(searchText, 'myth_desk', results.length);

  const capped = !expanded && results.length > preview;
  const shown = capped ? results.filter((s, i) => i < preview || popped.has(slugify(s.title))) : results;

  const isPopped = (slug) => allPopped || popped.has(slug);

  const showAll = () => {
    if (window.gtag) window.gtag('event', 'myth_show_all', { count: results.length });
    setExpanded(true);
  };

  // The reader is at the bottom of the long grid: after it shrinks, bring the
  // top rows back into view instead of leaving them further down the page.
  const showFewer = () => {
    track('myth_show_fewer', { count: results.length });
    setExpanded(false);
    setTimeout(() => scrollToEl(countRef.current), 0);
  };

  const togglePopAll = () => {
    if (window.gtag) window.gtag('event', 'myth_pop_all', { on: !allPopped });
    if (allPopped) { setAllPopped(false); setPopped(new Set()); } else { setAllPopped(true); }
  };

  return (
    <section className="fi-desk" id="myths" ref={deskRef} data-fi-desk data-track-context="myth_desk" aria-labelledby="fi-desk-h">
      <div className="fi-wrap">
        <div className="fi-desk-h">
          <div>
            <h2 className="fi-h2" id="fi-desk-h">The fact-check desk</h2>
            <p className="fi-sub">Every idiotic statement people repeat about EVs comes in a bubble. Pop one for the facts and the receipts, then send it to whoever said it.</p>
          </div>
          <p className="fi-rule"><b>House rule:</b> if it's true, we say so. {verdictCounts.true || 0} of them are, and we printed those too.</p>
        </div>

        <div className="fi-tools-row" ref={searchRef}>
          <label className="fi-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <span className="fi-sr">Search the idiotic statements</span>
            <input type="search" value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder={`Search ${sampleContent.length} idiotic statements: fires, cold, battery...`} />
          </label>
          <div className="fi-tabs" role="group" aria-label="Filter by verdict">
            {VERDICT_TABS.map((t) => (
              <button key={t.key} type="button" className={`fi-tab${verdict === t.key ? ' is-on' : ''}`} aria-pressed={verdict === t.key} onClick={() => { setVerdict(t.key); track('filter_change', { context: 'myth_desk', filter_type: 'verdict', filter_value: t.key, on: true }); }}>
                {t.label}<span>{t.key === 'all' ? sampleContent.length : (verdictCounts[t.key] || 0)}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="fi-tools-row">
          <button type="button" className="fi-btn fi-btn--sm" aria-expanded={showFilters} aria-controls="fi-desk-filters" onClick={() => { track('myth_filters_toggle', { open: !showFilters }); setShowFilters(!showFilters); }}>
            {showFilters ? 'Hide filters' : 'Filter by topic or idiot type'}{filters.length > 0 ? ` (${filters.length})` : ''}
          </button>
          <button type="button" className="fi-btn fi-btn--sm" onClick={togglePopAll}>
            {allPopped ? 'Re-inflate them all' : 'Pop them all'}
          </button>
        </div>

        {showFilters && (
          <div className="fi-filters" id="fi-desk-filters" ref={filtersRef}>
            <h3>Topic</h3>
            <div className="fi-chips">
              {TOPICS.map((t) => (
                <button key={t} type="button" className={`fi-chip${filters.includes(t) ? ' is-on' : ''}`} aria-pressed={filters.includes(t)} onClick={() => toggleFilter(t)}>{t}</button>
              ))}
            </div>
            <h3 style={{ marginTop: 14 }}>
              Idiot type <RouterLink to="/what-kind-of-idiot" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 700, color: '#111' }}>(not sure? take the quiz)</RouterLink>
            </h3>
            <div className="fi-chips">
              {IDIOTS.map((t) => (
                <button key={t} type="button" className={`fi-chip${filters.includes(t) ? ' is-on' : ''}`} aria-pressed={filters.includes(t)} onClick={() => toggleFilter(t)}>{t}</button>
              ))}
              {filters.length > 0 && <button type="button" className="fi-link-btn" onClick={() => { track('filter_change', { context: 'myth_desk', filter_type: 'clear_all', filter_value: filters.join('|'), on: false }); setFilters([]); }}>Clear all filters</button>}
            </div>
          </div>
        )}

        <div className="fi-count" aria-live="polite" ref={countRef}>
          <b>{results.length}</b>
          <span>
            {searchText ? `idiotic statements matching "${searchText}"` : filters.length > 0 ? `idiotic statements about ${filters.join(', ')}` : 'idiotic statements, fact-checked'}
          </span>
        </div>

        {results.length === 0 ? (
          <div className="fi-empty">No idiotic statements match that. Try another word or clear the filters.</div>
        ) : (
          <div className="fi-grid">
            {shown.map((s) => {
              const slug = slugify(s.title);
              return (
                <div key={s.id} data-card-slug={slug} ref={(el) => { cardRefs.current[slug] = el; }}>
                  {isPopped(slug) ? (
                    <div className="fi-popped">
                      <IdioticStatementCard cardDetails={s} onUnpop={() => unpop(slug)} />
                    </div>
                  ) : (
                    <article className="fi-bub">
                      <span className="fi-bno">Statement No. {checkNo(s)}</span>
                      <h3 className={`fi-bclaim${s.title.length > 40 ? ' fi-bclaim--long' : ''}`}>{s.title}</h3>
                      <button
                        type="button"
                        className="fi-btn fi-btn--pop fi-btn--sm"
                        onClick={() => { if (window.gtag) window.gtag('event', 'myth_pop', { title: s.title }); pop(slug, false); }}
                        aria-label={`Pop it: ${s.title}`}
                      >
                        Pop it
                      </button>
                    </article>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {results.length > preview && (
          <div className="fi-more">
            <button type="button" className="fi-btn" onClick={expanded ? showFewer : showAll}>
              {expanded ? 'Show fewer' : `Show all ${results.length} takes`}
            </button>
          </div>
        )}

        <nav className="fi-all-list" aria-label="Every fact check" data-track-context="myth_desk_all_list">
          <h3>Every idiotic statement we've checked</h3>
          <ul>
            {sampleContent.map((s) => (
              <li key={s.id}>
                <RouterLink to={mythPath(s)}>{s.title}</RouterLink>
                <span className={badgeClass(s)} style={{ padding: '2px 6px 0' }}>{s.titleBadge}</span>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </section>
  );
}
